/**
 * Shared UI atoms for the Connect Admin pages.
 *
 * These stay local to the connect/ folder: a page header (olive eyebrow +
 * title + subtitle matching the CoAs / CRM header), an access-denied card, a
 * "backend not available yet" placeholder for graceful degradation, a back
 * link (top-left ArrowLeft per the UI standard), and a centered Modal shell.
 *
 * All styling comes from the shared amp-* Tailwind tokens; no new design
 * language. See COCKPIT.md section 2.9.
 */

import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';

export function ConnectHeader({
  title,
  subtitle,
  actions,
  eyebrow = 'Connect',
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  eyebrow?: string;
}) {
  return (
    <div className="flex items-start justify-between mb-6 gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
          {eyebrow}
        </p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h1>
        {subtitle && (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
    >
      <ArrowLeft className="h-3 w-3" /> {label}
    </Link>
  );
}

export function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Connect is gated by the connect access scope. Ask IT to grant you access.
      </p>
    </div>
  );
}

// Quiet placeholder shown when a Connect backend slice is not deployed yet
// (a 404 or 503 from the API). Never a crash: the section that consumed the
// missing endpoint renders this card in its place.
export function NotAvailableCard({ what }: { what: string }) {
  return (
    <div className="rounded-lg border border-dashed border-amp-line bg-amp-cream-warm px-4 py-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">{what} is not available yet</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        This part of Connect is still being set up. It will appear here once the backend is live.
      </p>
    </div>
  );
}

export function ErrorCard({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white px-4 py-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm text-red-700 dark:text-red-300">Could not load: {message}</p>
    </div>
  );
}

// Centered modal shell (backdrop + card). Backdrop click and Escape close it,
// matching the NotesModal / sign-out modal conventions in the app.
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[85vh] overflow-hidden rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface flex flex-col`}
      >
        <div className="flex items-center justify-between border-b border-amp-line px-4 py-3 dark:border-amp-dark-line">
          <h2 className="text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-4 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-amp-line px-4 py-3 dark:border-amp-dark-line">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Shared control class strings so the connect pages match the CRM detail form.
export const inputCls =
  'w-full border border-amp-line rounded px-2.5 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';
export const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1';

export const primaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface';
export const secondaryBtnCls =
  'inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm';
